import {useLayoutEffect, useRef} from 'react'

import {GROUP_HEADER_HEIGHT} from '../styled-group-header'

const WAIT_REEVALUATE_TIMEOUT = 10
/**
 * Ensures that after a group is expanded or collapsed, the scroll position is adjusted so the
 * (potentially sticky) group header remains in view. This compensates for layout shifts that occur
 * during virtualization or async rendering.
 *
 * We "nudge" the scroll container twice (rAF + setTimeout) because a secondary layout shift can
 * happen right after the first paint. The second adjustment increases the chance the header ends up
 * in the intended position even at the cost of a minor flicker.
 */
export function useScrollGroupIntoViewOnToggle({
  isCollapsed,
  groupRef,
  scrollRef,
  headerRef,
}: {
  isCollapsed: boolean
  groupRef: React.RefObject<HTMLElement | null>
  scrollRef: React.RefObject<HTMLElement | null>
  headerRef: React.RefObject<HTMLElement | null>
}) {
  const collapsedRef = useRef(isCollapsed)

  useLayoutEffect(() => {
    if (isCollapsed !== collapsedRef.current) {
      let forceGroupScroll = false

      const getGroupPosition = () => {
        if (!groupRef.current || !scrollRef.current || !headerRef.current) return 0
        // Position of grouped items relative to viewport
        const {top: itemsTop} = groupRef.current.getBoundingClientRect()
        // Position of scroll container relative to viewport
        const {top: containerTop} = scrollRef.current.getBoundingClientRect()
        // Height of sticky header and group header
        const totalHeaderHeight = headerRef.current.getBoundingClientRect().height + GROUP_HEADER_HEIGHT

        return itemsTop - totalHeaderHeight - containerTop
      }

      const scrollToGroup = () => {
        requestAnimationFrame(() => {
          if (!scrollRef.current) return
          const groupPosition = getGroupPosition()
          if (forceGroupScroll || groupPosition < 0) {
            scrollRef.current.scrollTop = scrollRef.current.scrollTop + groupPosition
            // After our initial adjustment, call this function again (via the timeout) regardless of scroll
            // position in case another layout shift changes the group position.
            forceGroupScroll = true
          }
        })
      }

      scrollToGroup()
      // Occasionally a second layout shift occurs right after the initial rendering of items. This can result
      // in an incorrect original scroll adjustment. Scrolling a second time can cause a small visual flicker
      // but increases the success of scrolling to a group.
      // eslint-disable-next-line @eslint-react/web-api/no-leaked-timeout
      window.setTimeout(() => {
        scrollToGroup()
      }, WAIT_REEVALUATE_TIMEOUT)
    }
    collapsedRef.current = isCollapsed
  }, [isCollapsed, groupRef, headerRef, scrollRef])
}
